import {
  ButtonLoading,
  Divider,
  SectionHeader,
  TextCopy,
  TextField,
  TrevorIcon,
} from '@trevor/ui-components';
import React from 'react';
import styled from 'styled-components';
import { useDashboardId, useDatasourceId } from '@/hooks';
import {
  useGetInvitesQuery,
  useSendInvitationMutation,
} from '@/features/dashboardShare/ShareDropdown/InviteTab/inviteApi';
import checkEmail from '@/features/dashboardShare/ShareDropdown/InviteTab/checkEmail';
import { useSelector } from 'react-redux';
import { InvitedUser } from '@/features/dashboard/components/SharePublishEmbedSidebar/components/InvitedUser';
import { getActiveDashboardName } from '@/store-selectors';
import { useDashboard } from '@/hooks/useDashboard';
import { LearMoreBanner } from '@/features/dashboard/components/SharePublishEmbedSidebar/components/LearnMoreBanner';
import { userAction } from '@/util/helpers/ajax-helpers';

const TabContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-l);
`;

const InviteForm = styled.form`
  display: flex;
  gap: var(--spacing-s);
  width: 100%;
  justify-content: space-between;
`;

const InviteListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-s);
`;

export const SharedWithOthersTab = () => {
  const datasourceId = useDatasourceId();
  const dashboardId = useDashboardId();
  const dashboardIdName = useSelector(getActiveDashboardName);
  const { shareDashboardPrivateUrl, shareDashboardPublicUrl } = useDashboard();
  const [userToInvite, setUserToInvite] = React.useState('');

  const [sendInvitation, { isLoading: sendInvitationLoading }] =
    useSendInvitationMutation();

  const { data: emails = [] } = useGetInvitesQuery(
    {
      datasourceId,
      dashboardId,
    },
    { skip: !dashboardId },
  );

  const [inviteErrorMessage, setInviteErrorMessage] =
    React.useState<Maybe<string>>(null);

  const invitedUsersCount = emails.length;

  const handleInviteUser = async (e: React.FormEvent<SubmitEvent>) => {
    e.preventDefault();
    if (!checkEmail(userToInvite)) {
      setInviteErrorMessage('The email you entered is not valid');
      return;
    }

    if (emails.includes(userToInvite)) {
      setInviteErrorMessage('The email you entered is duplicated');
      return;
    }

    await sendInvitation({
      email: userToInvite,
      resourceName: dashboardIdName,
      resourceId: dashboardId,
      datasourceId,
      resourceType: 'DASHBOARD',
      accessType: 'READ',
    });
    userAction('embed-dashboards-sidebar-share-with-others-tab', {
      action: 'invite-user',
      value: userToInvite,
    });

    setUserToInvite('');
    setInviteErrorMessage(null);
  };

  return (
    <TabContent key="share-with-others">
      {shareDashboardPublicUrl && (
        <>
          <SectionHeader
            iconName="web"
            title="Share with anyone"
            subtitle="Anyone with this link can view this dashboard"
          />
          <TextCopy
            isUrl
            text={shareDashboardPublicUrl}
            onCopy={() =>
              userAction('embed-dashboards-sidebar-share-with-others-tab', {
                action: 'share-with-anyone-url-copy',
              })
            }
            onOpen={() =>
              userAction('embed-dashboards-sidebar-share-with-others-tab', {
                action: 'share-with-anyone-url-open',
              })
            }
          />
          <Divider />
        </>
      )}
      <SectionHeader
        iconName="team"
        title="Share with team"
        subtitle="Share dashboard with people with access to Trevor"
      />
      <TextCopy
        isUrl
        text={shareDashboardPrivateUrl}
        onCopy={() =>
          userAction('embed-dashboards-sidebar-share-with-others-tab', {
            action: 'share-with-team-url-copy',
          })
        }
        onOpen={() =>
          userAction('embed-dashboards-sidebar-share-with-others-tab', {
            action: 'share-with-team-url-open',
          })
        }
      />
      <Divider />
      <SectionHeader
        iconName="user-add"
        title={`Invite guests (${invitedUsersCount})`}
        subtitle="Guests can only access the dashboards they're invited to."
      />
      <InviteForm onSubmit={handleInviteUser}>
        <TextField
          width="100%"
          value={userToInvite}
          onChange={(value) => setUserToInvite(value)}
          placeholder="Add email address"
          error={inviteErrorMessage}
        />

        <ButtonLoading
          type="submit"
          loading={sendInvitationLoading}
          variant="secondary"
          icon={<TrevorIcon name="send-invitation-envelope" />}
          disabled={userToInvite.length === 0}
        >
          Send invitation
        </ButtonLoading>
      </InviteForm>
      <InviteListContainer>
        {emails.map((email) => (
          <InvitedUser key={email} email={email} />
        ))}
      </InviteListContainer>
      <LearMoreBanner />
    </TabContent>
  );
};
